import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import 'jspdf-autotable';
import { FiSearch, FiTrash2, FiFileText, FiDownload, FiClock, FiX, FiChevronDown } from 'react-icons/fi';

import { API_BASE_URL } from '../lib/session';
const CARD =
  'rounded-3xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.05)]';

const ExportButton = ({ label, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex h-10 items-center gap-1.5 rounded-full border-0 bg-black/[0.04] px-[16px] text-[13px] font-medium text-ink transition hover:bg-black/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 disabled:cursor-not-allowed disabled:opacity-40"
  >
    <FiDownload size={14} aria-hidden="true" />
    {label}
  </button>
);

const LOG_PAGE_SIZE = 25;

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(LOG_PAGE_SIZE);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetch(`${API_BASE_URL}/api/alerts`)
        .then((response) => response.json())
        .then((data) => setAlerts(data))
        .catch((err) => console.error('Erreur lors de la récupération des alertes', err))
        .finally(() => setHasLoaded(true));
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    setVisibleCount(LOG_PAGE_SIZE);
  }, [searchTerm]);

  const alertList = Array.isArray(alerts) ? alerts : [];

  const filteredAlerts = alertList.filter((alert) =>
    (alert.message || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // The API returns entries oldest-first; a journal reads newest-first.
  const visibleAlerts = filteredAlerts.slice(-visibleCount).reverse();
  const hiddenCount = filteredAlerts.length - visibleAlerts.length;

  const handleDeleteAlert = (id) => {
    if(window.confirm("Confirmez la suppression !")) {
      fetch(`${API_BASE_URL}/api/alerts/${id}`, {
        method: 'DELETE',
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.alert) {
            setAlerts(alertList.filter((alert) => alert._id !== id));
          }
        })
        .catch((err) => console.error('Erreur lors de la suppression de l\'alerte', err));
    }
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.text("Liste des actions effectuées par les utilisateurs", 14, 10);
    doc.autoTable({
      head: [['Notification(s)']],
      body: filteredAlerts.map(alert => [alert.message]),
    });
    doc.save('alertes_utilisateurs.pdf');
  };

  const downloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredAlerts.map(alert => ({
      Message: alert.message,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Alertes");
    XLSX.writeFile(wb, 'alertes_utilisateurs.xlsx');
  };

  const downloadWord = () => {
    const doc = new Blob([`
      Liste des alertes effectuées par les utilisateurs\n\n
      ${filteredAlerts.map(alert => ` Message: ${alert.message}`).join('\n')}
    `], { type: 'application/msword' });
    saveAs(doc, 'alertes_utilisateurs.doc');
  };

  const entryCount = alertList.length;
  const canExport = hasLoaded && filteredAlerts.length > 0;

  return (
    <section aria-label="Actions des utilisateurs" className={CARD}>
      <div className="flex flex-col gap-[16px] border-x-0 border-t-0 border-b border-solid border-black/[0.05] p-[20px] sm:p-6 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center gap-[12px]">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-2xl bg-ink text-white">
            <FiClock size={18} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 className="m-0 text-[17px] font-semibold tracking-tight text-ink">
              Actions des utilisateurs
            </h2>
            <p className="m-0 text-[13px] text-black/45">
              {hasLoaded
                ? `${entryCount.toLocaleString('fr-FR')} ${entryCount > 1 ? 'entrées' : 'entrée'} · les plus récentes en premier`
                : 'Chargement du journal…'}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-[12px] sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-64">
            <FiSearch
              size={16}
              aria-hidden="true"
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-black/35"
            />
            <input
              type="text"
              aria-label="Rechercher dans le journal"
              placeholder="Rechercher une action…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-10 w-full rounded-full border-[1px] border-solid border-black/[0.08] bg-mist pl-10 pr-4 text-[14px] text-ink outline-none transition placeholder:text-black/35 focus:border-brand-600 focus:bg-white focus:ring-4 focus:ring-brand-600/10"
            />
          </div>
          <div role="group" aria-label="Exporter le journal" className="flex gap-2">
            <ExportButton label="PDF" onClick={downloadPDF} disabled={!canExport} />
            <ExportButton label="Excel" onClick={downloadExcel} disabled={!canExport} />
            <ExportButton label="Word" onClick={downloadWord} disabled={!canExport} />
          </div>
        </div>
      </div>

      {!hasLoaded ? (
        <ul aria-hidden="true" className="m-0 list-none p-0">
          {[0, 1, 2, 3].map((i) => (
            <li
              key={i}
              className="flex items-center gap-[16px] border-x-0 border-t-0 border-b border-solid border-black/[0.04] px-[20px] py-[16px] last:border-b-0 sm:px-6"
            >
              <span className="h-8 w-8 flex-none animate-pulse rounded-xl bg-black/[0.05]" />
              <span className="h-3 flex-1 animate-pulse rounded-full bg-black/[0.05]" style={{ maxWidth: `${70 - i * 12}%` }} />
            </li>
          ))}
        </ul>
      ) : entryCount === 0 ? (
        <div className="flex flex-col items-center px-6 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-mist text-black/40">
            <FiClock size={20} aria-hidden="true" />
          </span>
          <p className="mb-0 mt-[16px] text-[15px] font-medium text-ink">Aucune action enregistrée</p>
          <p className="mb-0 mt-1 max-w-sm text-[14px] text-black/45">
            Les actions effectuées par les utilisateurs de la plateforme apparaîtront ici.
          </p>
        </div>
      ) : filteredAlerts.length === 0 ? (
        <div className="flex flex-col items-center px-6 py-16 text-center">
          <p className="m-0 text-[15px] font-medium text-ink">
            Aucune action ne correspond à « {searchTerm} »
          </p>
          <button
            type="button"
            onClick={() => setSearchTerm('')}
            className="mt-[16px] flex h-10 items-center gap-1.5 rounded-full border-0 bg-black/[0.04] px-[16px] text-[13px] font-medium text-ink transition hover:bg-black/[0.08]"
          >
            <FiX size={14} aria-hidden="true" />
            Effacer la recherche
          </button>
        </div>
      ) : (
        <>
        <ul className="m-0 list-none p-0">
          {visibleAlerts.map((alert) => (
            <li
              key={alert._id}
              className="flex items-start gap-[16px] border-x-0 border-t-0 border-b border-solid border-black/[0.04] px-[20px] py-[16px] transition last:border-b-0 hover:bg-mist/60 sm:px-6"
            >
              <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-xl bg-mist text-black/50">
                <FiFileText size={15} aria-hidden="true" />
              </span>
              <p className="m-0 min-w-0 flex-1 break-words pt-1 text-[14px] leading-relaxed text-ink">
                {alert.message}
              </p>
              <button
                type="button"
                onClick={() => handleDeleteAlert(alert._id)}
                aria-label="Supprimer cette entrée"
                title="Supprimer"
                className="flex h-10 w-10 flex-none items-center justify-center rounded-full border-0 bg-[transparent] text-black/35 transition hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 sm:h-9 sm:w-9"
              >
                <FiTrash2 size={16} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
        {hiddenCount > 0 && (
          <div className="flex flex-col items-center gap-[12px] border-x-0 border-b-0 border-t border-solid border-black/[0.05] px-6 py-[20px] sm:flex-row sm:justify-between">
            <p className="mb-0 text-[13px] tabular-nums text-black/45">
              {visibleAlerts.length.toLocaleString('fr-FR')} sur {filteredAlerts.length.toLocaleString('fr-FR')} affichées
            </p>
            <button
              type="button"
              onClick={() => setVisibleCount((count) => count + LOG_PAGE_SIZE)}
              className="flex h-10 items-center gap-1.5 rounded-full border-0 bg-black/[0.05] px-[20px] text-[14px] font-medium text-ink transition hover:bg-black/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40"
            >
              <FiChevronDown size={15} aria-hidden="true" />
              Afficher {Math.min(LOG_PAGE_SIZE, hiddenCount)} de plus
            </button>
          </div>
        )}
        </>
      )}
    </section>
  );
};

export default Alerts;
